// External libraries
import { toast } from "@/hooks/use-toast";

// Internal imports
import type { ApiResponse } from "@shared/types/api";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: any,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// Token getter function - set by the auth store
let tokenGetter: () => string | null = () => null;

export function setTokenGetter(getter: () => string | null) {
  tokenGetter = getter;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = "") {
    const envBase = (import.meta as any).env?.VITE_API_URL || baseUrl || "";
    this.baseUrl = envBase;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    // Token is handled via httpOnly cookie (credentials: "include")
    // No need for Authorization header

    // CSRF protection: Read token from cookie and add to header
    const csrfToken = this.getCSRFToken();
    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    return headers;
  }

  private getCSRFToken(): string | null {
    try {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'csrf_token') {
          return value;
        }
      }
    } catch (error) {
      console.error('[API Client] Failed to read CSRF token:', error);
    }
    return null;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorData;
      try {
        const json = await response.json();
        errorData = json.error || json;
      } catch {
        errorData = { message: response.statusText };
      }

      throw new ApiError(
        errorData.message || errorData.error?.message || "Request failed",
        response.status,
        errorData.code || errorData.error?.code,
        errorData.details || errorData.error?.details,
      );
    }

    try {
      const json = await response.json();
      // Handle both formats: { success: true, data: {...} } and { success: true, token, user, ... }
      if (json.success && json.data) {
        return json.data as T;
      }
      // Return the whole response if it has success but no data property (e.g., auth responses)
      return json as T;
    } catch {
      return {} as T;
    }
  }

  async get<T>(url: string, params?: Record<string, any>): Promise<T> {
    const searchParams = params ? new URLSearchParams(params).toString() : "";
    const fullUrl = `${this.baseUrl}${url}${searchParams ? `?${searchParams}` : ""}`;

    const response = await fetch(fullUrl, {
      credentials: "include",
      headers: this.getHeaders(),
    });

    return this.handleResponse<T>(response);
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response = await fetch(`${this.baseUrl}${url}`, {
      method: "POST",
      headers: this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });

    return this.handleResponse<T>(response);
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await fetch(`${this.baseUrl}${url}`, {
      method: "PUT",
      headers: this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });

    return this.handleResponse<T>(response);
  }

  async patch<T>(url: string, data?: any): Promise<T> {
    const response = await fetch(`${this.baseUrl}${url}`, {
      method: "PATCH",
      headers: this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(url: string): Promise<T> {
    const response = await fetch(`${this.baseUrl}${url}`, {
      method: "DELETE",
      credentials: "include",
      headers: this.getHeaders(),
    });

    return this.handleResponse<T>(response);
  }
}

export const apiClient = new ApiClient();
